import { useEffect, useMemo, useState } from "react";
import { buildPreview, PREVIEW_SANDBOX } from "@buildlab/preview";
import { exitTicketLine } from "@buildlab/shared/exit-ticket"; // not the package root: that would ship the screening lists to browsers
import { api, type ExitTicketScore, type ParentLinkInfo, type SubmissionDetail, type SubmissionListItem, type TranscriptTurn, lessonLabel } from "./api";
import { Alert, Back, Check, Restart } from "./icons";
import { LevelUpChip } from "./LevelUpChip";
import "./grading.css";

// The teacher's grading view (PRD FR-24, §5.3 drill-down). The app gathers evidence: reflection
// answers, grading-hook checks, a playable snapshot, the transcript and an optional AI summary of
// the process. Exit tickets are scored automatically out of 10 (decision 86); that score is the grade
// until the teacher types their own, which always wins.

const when = (t: number) => {
  const d = new Date(t);
  const today = new Date().toDateString() === d.toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return today ? `Today, ${time}` : `${d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}, ${time}`;
};

/** The teacher typed a grade (a saved comment alone keeps the automatic score). */
const teacherSet = (s: { score: string | null; gradedAt: number | null }) => !!s.gradedAt && !!s.score?.trim();

const PROBLEM_WORDS: Record<ExitTicketScore["missing"]["answers"][number]["problem"], string> = {
  empty: "blank",
  short: "fewer than 5 words",
  filler: "mostly filler (\"idk\")",
  repeat: "the same words repeated",
  gibberish: "random letters",
  copy: "copied the question",
};

// The capstone rubric's 4-point scale (CURRICULUM §6); any other score can be typed in.
const QUICK_SCORES = [
  { score: "4", label: "Exceeds" },
  { score: "3", label: "Meets" },
  { score: "2", label: "Approaching" },
  { score: "1", label: "Beginning" },
];

/** Home-page entry point: how many submissions are waiting for a grade. */
export function SubmissionsEntry({ onOpen }: { onOpen: () => void }) {
  const [list, setList] = useState<SubmissionListItem[] | null>(null);
  useEffect(() => {
    void api.teacherSubmissions().then((r) => setList(r.submissions), () => setList([]));
  }, []);
  if (!list) return null;
  const waiting = list.filter((s) => !teacherSet(s)).length;
  return (
    <section aria-label="Submissions" className="subs-entry">
      <div>
        <h2>Submissions</h2>
        <p className="muted small">
          {list.length === 0
            ? "Nothing submitted yet. Submitted projects show up here with their reflections and evidence."
            : `${list.length} project${list.length === 1 ? "" : "s"} submitted · exit tickets scored automatically · ${waiting} without your own grade`}
        </p>
      </div>
      <button type="button" className="primary" onClick={onOpen}>
        Open submissions
      </button>
    </section>
  );
}

// The teacher's last-picked lesson (a per-viewer convenience; may be unavailable).
const LESSON_KEY = "buildlab-submissions-lesson";
const savedLesson = () => {
  try {
    return localStorage.getItem(LESSON_KEY);
  } catch {
    return null;
  }
};

export function Submissions({ onExit, onOpenProject }: { onExit: () => void; onOpenProject: (id: string) => void }) {
  const [list, setList] = useState<SubmissionListItem[] | null>(null);
  const [lessons, setLessons] = useState<{ id: string; lesson: number; title: string }[]>([]);
  const [lessonId, setLessonId] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [onlyUngraded, setOnlyUngraded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.teacherSubmissions().then(
      (r) => {
        setList(r.submissions);
        // Open on the lesson picked last time, else the one with the newest submission.
        const newest = [...r.submissions].sort((a, b) => b.submittedAt - a.submittedAt)[0];
        const saved = savedLesson();
        setLessonId(saved && r.submissions.some((s) => s.assignment.id === saved) ? saved : (newest?.assignment.id ?? null));
      },
      (e: Error) => setError(e.message),
    );
    api.assignments().then(
      (r) => setLessons(r.assignments.map(({ id, lesson, title }) => ({ id, lesson, title }))),
      () => {}, // the picker falls back to lessons that have submissions
    );
  }, []);

  // A lesson browser (Paul, Oct 19): every lesson in course order with its counts; the list shows one
  // lesson's submissions, newest first.
  const lessonTabs = useMemo(() => {
    const all = new Map(lessons.map((l) => [l.id, { ...l, total: 0 }]));
    for (const s of list ?? []) {
      const t = all.get(s.assignment.id) ?? { id: s.assignment.id, lesson: s.assignment.lesson, title: s.assignment.title, total: 0 };
      t.total++;
      all.set(s.assignment.id, t);
    }
    return [...all.values()].sort((a, b) => a.lesson - b.lesson);
  }, [lessons, list]);

  const inLesson = useMemo(() => (list ?? []).filter((s) => s.assignment.id === lessonId).sort((a, b) => b.submittedAt - a.submittedAt), [list, lessonId]);
  const shown = inLesson.filter((s) => !onlyUngraded || !teacherSet(s));
  const avg = inLesson.length ? inLesson.reduce((n, s) => n + s.exitTicket.score, 0) / inLesson.length : 0;

  // Picking a lesson opens its first submission still to grade.
  useEffect(() => {
    setSelected((inLesson.find((s) => !teacherSet(s)) ?? inLesson[0])?.id ?? null);
  }, [lessonId, list === null]);

  const pickLesson = (id: string) => {
    setLessonId(id);
    try {
      localStorage.setItem(LESSON_KEY, id);
    } catch {}
  };

  const onGraded = (projectId: string, score: string, gradedAt: number) =>
    // An empty score means "use the automatic one".
    setList((all) => (all ?? []).map((s) => (s.projectId === projectId ? { ...s, score, gradedAt } : s)));

  const current = lessonTabs.find((t) => t.id === lessonId);

  return (
    <div className="grading">
      <header className="gbar">
        <button type="button" className="pill-dark back" onClick={onExit}>
          <Back />
          Home
        </button>
        <div className="title">
          <span className="eyebrow gold">BuildLab · Teacher</span>
          <h1>Submissions</h1>
        </div>
        <label className="toggle">
          <input type="checkbox" checked={onlyUngraded} onChange={(e) => setOnlyUngraded(e.target.checked)} />
          Only not graded by me
        </label>
        <DownloadExitTickets />
      </header>

      {error && <p className="page-error">{error}</p>}
      {!list && !error && <p className="loading">Loading submissions…</p>}
      {list && list.length === 0 && <p className="loading">Nothing submitted yet. When a project is submitted, it shows up here with its reflection and evidence.</p>}

      {list && list.length > 0 && (
        <>
          <nav className="lesson-pick" aria-label="Lessons">
            {lessonTabs.map((t) => (
              <button key={t.id} type="button" aria-pressed={t.id === lessonId} className={t.total ? "" : "empty"} onClick={() => pickLesson(t.id)}>
                <span className="eyebrow">{lessonLabel(t.lesson)}</span>
                <span className="lp-title">{t.title}</span>
                <span className="lp-count">{t.total ? `${t.total} submitted` : "None yet"}</span>
              </button>
            ))}
          </nav>
          <div className="grading-body">
            <nav className="sub-list" aria-label={current ? `${lessonLabel(current.lesson)} submissions` : "Submissions"}>
              {current && (
                <header className="sub-lesson">
                  <h2 className="eyebrow">
                    {lessonLabel(current.lesson)} · {current.title}
                  </h2>
                  {inLesson.length > 0 && (
                    <p className="muted small">
                      {inLesson.length} {inLesson.length === 1 ? "student" : "students"} · exit tickets average {avg.toFixed(1)}/10
                    </p>
                  )}
                </header>
              )}
              {inLesson.length === 0 && <p className="muted small pad">Nothing submitted for this lesson yet.</p>}
              {inLesson.length > 0 && shown.length === 0 && <p className="muted small pad">Everything in this lesson is graded.</p>}
              <ul>
                {shown.map((s) => (
                  <li key={s.id}>
                    <button type="button" aria-current={selected === s.id ? "true" : undefined} onClick={() => setSelected(s.id)}>
                      <span className="sname">
                        {s.studentName}
                        {s.demo && <span className="badge-demo">Demo</span>}
                      </span>
                      <span className="smeta">
                        {s.projectTitle} · {when(s.submittedAt)}
                        {s.attempts > 1 ? ` · ${s.attempts} versions` : ""}
                      </span>
                      <span className="row">
                        <span className={`chip ${s.evidence.passed === s.evidence.total ? "ok" : ""}`}>
                          {s.evidence.passed}/{s.evidence.total} checks
                        </span>
                        <span className={`chip auto ${s.exitTicket.score === s.exitTicket.outOf ? "full" : ""}`} title="Exit ticket, scored automatically">
                          Exit ticket {s.exitTicket.score}/{s.exitTicket.outOf}
                          {teacherSet(s) ? "" : " · auto"}
                        </span>
                        {teacherSet(s) && <span className="chip graded">Your grade: {s.score}</span>}
                        {s.levelUps && <LevelUpChip count={s.levelUps} />}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
            {selected !== null ? (
              <SubmissionView key={selected} id={selected} onSelect={setSelected} onGraded={onGraded} onOpenProject={onOpenProject} />
            ) : (
              <p className="loading">{inLesson.length ? "Pick a submission." : "Pick a lesson with submissions."}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Teachers: every student's latest exit ticket per lesson, as Markdown files in a zip (decision 86). */
function DownloadExitTickets() {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  return (
    <span className="download row">
      <button
        type="button"
        className="pill-dark"
        id="download-exit-tickets"
        title="A zip with one file per student per lesson (First L.md), for Claude Code"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setNote(null);
          try {
            const r = await api.downloadExitTickets();
            setNote(`${r.files} exit ticket${r.files === 1 ? "" : "s"}`);
          } catch (e) {
            setNote((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "Preparing…" : "Download exit tickets"}
      </button>
      {note && <span className="small download-note">{note}</span>}
    </span>
  );
}

/** The automatic exit-ticket score and why it isn't 10. */
function ExitTicketPanel({ ticket, teacherScore }: { ticket: ExitTicketScore; teacherScore: string | null }) {
  return (
    <section className="panel exit-ticket" aria-label="Exit ticket score">
      <div className="row between">
        <span className="eyebrow">Exit ticket · scored automatically</span>
        <span className="muted small">{teacherScore ? `Your grade (${teacherScore}) replaces it` : "This is the grade unless you set one"}</span>
      </div>
      <p className="ticket-line">
        <b>{exitTicketLine(ticket)}</b>
      </p>
      <p className="muted small">
        {ticket.answers.counted} of {ticket.answers.total} answers count (5+ words, not junk) · {ticket.checks.passed} of {ticket.checks.total} required lesson checks passed. Bonus checks never count
        against.
      </p>
      {(ticket.missing.answers.length > 0 || ticket.missing.checks.length > 0) && (
        <ul>
          {ticket.missing.answers.map((m) => (
            <li key={`a${m.index}`}>
              Answer {m.index + 1}: {PROBLEM_WORDS[m.problem]}
            </li>
          ))}
          {ticket.missing.checks.map((c) => (
            <li key={`c${c.id}`}>Check not passed: {c.label}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SubmissionView({
  id,
  onSelect,
  onGraded,
  onOpenProject,
}: {
  id: number;
  onSelect: (id: number) => void;
  onGraded: (projectId: string, score: string, gradedAt: number) => void;
  onOpenProject: (id: string) => void;
}) {
  const [data, setData] = useState<SubmissionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.teacherSubmission(id).then(setData, (e: Error) => setError(e.message));
  }, [id]);

  if (error) return <p className="page-error">{error}</p>;
  if (!data) return <p className="loading">Opening the submission…</p>;
  const s = data.submission;
  const latest = data.history[0]?.id === s.id;
  const required = s.evidence.filter((e) => !e.bonus);

  return (
    <article className="sub-detail" aria-label={`${s.studentName}'s submission`}>
      <header className="panel sub-head">
        <div className="row between">
          <div>
            <span className="eyebrow">
              {lessonLabel(s.assignment.lesson)} · {s.assignment.title}
              {s.assignment.practice ? ` · practicing ${s.assignment.practice}` : ""}
            </span>
            <h2>
              {s.studentName}
              {s.demo && <span className="badge-demo">Demo</span>}
            </h2>
            <p className="muted small">
              {s.projectTitle} · submitted {when(s.submittedAt)}
              {latest ? "" : " · an earlier version"}
              {s.levelUps && (
                <>
                  {" "}
                  <LevelUpChip count={s.levelUps} />
                </>
              )}
            </p>
          </div>
          <button type="button" className="outline small" onClick={() => onOpenProject(s.projectId)}>
            Open the project as it is now
          </button>
        </div>
        {data.history.length > 1 && (
          <div className="versions" role="group" aria-label="Versions">
            {[...data.history].reverse().map((h, i) => (
              <button key={h.id} type="button" aria-pressed={h.id === s.id} onClick={() => onSelect(h.id)}>
                Version {i + 1} · {when(h.submittedAt)} · {h.exitTicket.score}/{h.exitTicket.outOf}
                {h.gradedAt ? " · graded" : ""}
              </button>
            ))}
          </div>
        )}
      </header>

      <div className="sub-cols">
        <div className="sub-col">
          <ExitTicketPanel ticket={s.exitTicket} teacherScore={teacherSet(s) ? s.score : null} />

          <section className="panel" aria-label="Reflection">
            <span className="eyebrow">Exit ticket · the student's answers</span>
            {s.reflection.map((r, i) => {
              const miss = s.exitTicket.missing.answers.find((m) => m.index === i);
              return (
                <div key={i} className="qa">
                  <b>{r.question}</b>
                  <p>{r.answer || <i className="muted">(no answer)</i>}</p>
                  {miss && <span className="not-counted">Doesn't count: {PROBLEM_WORDS[miss.problem]}</span>}
                </div>
              );
            })}
          </section>

          <section className="panel" aria-label="Evidence">
            <div className="row between">
              <span className="eyebrow">Evidence · checked when submitted</span>
              <span className="muted small">
                {required.filter((e) => e.pass).length} of {required.length} checks
              </span>
            </div>
            <ul className="evidence">
              {s.evidence.map((e) => (
                <li key={e.id} className={e.pass ? "pass" : e.bonus ? "bonus" : "fail"}>
                  <span className="emark" aria-hidden="true">
                    {e.pass ? <Check size={14} stroke="#fff" width={3} /> : e.bonus ? "+" : <Alert size={14} stroke="#fff" width={2.5} />}
                  </span>
                  <span>
                    <b>
                      {e.label}
                      {e.bonus ? <span className="bonus-tag">Bonus</span> : null}
                    </b>
                    <span className="sr-only">{e.pass ? " (yes)" : " (not yet)"}</span>
                    <span className="edetail">{e.detail}</span>
                  </span>
                </li>
              ))}
              {/* Predict before play (D47): what they guessed before playing a given program. Evidence to read; not scored. */}
              {s.prediction && (
                <li className="predict bonus predict-evidence">
                  <span className="emark" aria-hidden="true">?</span>
                  <span>
                    <b>
                      Predicted before playing
                      <span className="bonus-tag">Not scored</span>
                    </b>
                    <span className="edetail">
                      {s.prediction.question} They picked: <b>{s.prediction.choice}</b>
                    </span>
                  </span>
                </li>
              )}
            </ul>
          </section>

          <ProcessSummary id={s.id} initial={s.summary} />
          <GradeForm
            key={s.id}
            id={s.id}
            score={s.score}
            comment={s.comment}
            gradedAt={s.gradedAt}
            auto={s.exitTicket}
            capstone={s.assignment.id === "l11-capstone"}
            onSaved={(score, at) => latest && onGraded(s.projectId, score, at)}
          />
          <ParentLinks id={s.id} />
        </div>

        <div className="sub-col">
          <SnapshotPreview files={s.files} />
          <Transcript turns={data.turns} />
        </div>
      </div>
    </article>
  );
}

/** Brag to your parents (decision 83): the links this student emailed their parents, with Revoke. */
function ParentLinks({ id }: { id: number }) {
  const [data, setData] = useState<{ links: ParentLinkInfo[]; featureOn: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => api.parentLinks(id).then(setData, (e: Error) => setError(e.message));
  useEffect(() => {
    void load();
  }, [id]);
  if (error) return <p className="dlg-error">{error}</p>;
  if (!data || data.links.length === 0) return null;
  return (
    <section className="panel parent-links" aria-label="Sent to parents">
      <span className="eyebrow">Sent to parents · Brag to parents</span>
      {!data.featureOn && <p className="muted small">Brag to parents is off, so none of these links open right now.</p>}
      <ul className="parent-link-list">
        {data.links.map((l) => (
          <li key={l.token}>
            <div>
              <b>
                Sent {when(l.createdAt)} to {l.recipients} {l.recipients === 1 ? "parent" : "parents"}
              </b>
              {l.sentTo.length > 0 && <span className="muted small"> ({l.sentTo.join(", ")})</span>}
              {l.submissionId !== id && <span className="muted small"> · another version</span>}
              {l.note && <p className="small">Note: {l.note}</p>}
              <p className="small">
                {l.revokedAt ? (
                  <span className="muted">Link turned off {when(l.revokedAt)}</span>
                ) : (
                  <a href={`/p/${l.token}`} target="_blank" rel="noreferrer">
                    Open the parent link
                  </a>
                )}
              </p>
            </div>
            {!l.revokedAt && (
              <button
                type="button"
                className="outline small"
                onClick={async () => {
                  try {
                    await api.revokeParentLink(l.token);
                    await load();
                  } catch (e) {
                    setError((e as Error).message);
                  }
                }}
              >
                Revoke link
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ProcessSummary({ id, initial }: { id: number; initial: string | null }) {
  const [summary, setSummary] = useState(initial);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <section className="panel" aria-label="Process summary">
      <span className="eyebrow">How they worked · AI summary</span>
      {summary ? (
        <p className="summary">{summary}</p>
      ) : (
        <>
          <p className="muted small">A short summary of the student's requests and the helper's replies. It reads only those, never names.</p>
          <div className="row">
            <button
              type="button"
              className="outline small"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const r = await api.submissionSummary(id);
                  setSummary(r.summary);
                  if (!r.summary) setNote(r.reason ?? "The summary didn't come back. Try again in a moment.");
                } catch (e) {
                  setNote((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Summarizing…" : "Summarize the process"}
            </button>
            {note && <span className="muted small">{note}</span>}
          </div>
        </>
      )}
    </section>
  );
}

function GradeForm({
  id,
  score,
  comment,
  gradedAt,
  auto,
  capstone,
  onSaved,
}: {
  id: number;
  score: string | null;
  comment: string | null;
  gradedAt: number | null;
  auto: ExitTicketScore;
  /** The 4/3/2/1 rubric buttons are for the capstone only (decision 87). */
  capstone: boolean;
  onSaved: (score: string, at: number) => void;
}) {
  const [value, setValue] = useState(score ?? "");
  const [text, setText] = useState(comment ?? "");
  const [saved, setSaved] = useState<number | null>(gradedAt);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.gradeSubmission(id, value, text);
      setSaved(r.gradedAt);
      setDirty(false);
      onSaved(value.trim(), r.gradedAt);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel grade" aria-label="Grade">
      <span className="eyebrow">Grade</span>
      <p className="which">
        {value.trim() ? (
          <>
            <b>Your grade: {value.trim()}</b> (replaces the automatic {auto.score}/{auto.outOf})
          </>
        ) : (
          <>
            <b>
              Automatic: {auto.score}/{auto.outOf}
            </b>{" "}
            · type or pick a score to change it
          </>
        )}
      </p>
      <div className="row">
        <label className="score">
          <span className="sr-only">Score</span>
          <input
            id="grade-score"
            value={value}
            maxLength={40}
            placeholder={`${auto.score}/${auto.outOf}`}
            onChange={(e) => {
              setValue(e.target.value);
              setDirty(true);
            }}
          />
        </label>
        {value.trim() && (
          <button
            type="button"
            className="qscore"
            title="Clear your score and use the automatic one"
            onClick={() => {
              setValue("");
              setDirty(true);
            }}
          >
            {auto.score}/{auto.outOf} <span>Automatic</span>
          </button>
        )}
        {capstone && QUICK_SCORES.map((q) => (
          <button
            key={q.score}
            type="button"
            className="qscore"
            aria-pressed={value === q.score}
            title={q.label}
            onClick={() => {
              setValue(q.score);
              setDirty(true);
            }}
          >
            {q.score} <span>{q.label}</span>
          </button>
        ))}
      </div>
      <label className="reflect">
        <span>Comment for the student</span>
        <textarea
          id="grade-comment"
          rows={3}
          maxLength={4000}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setDirty(true);
          }}
        />
      </label>
      <div className="row between">
        <span className="muted small">{error ? <span className="dlg-error">{error}</span> : saved && !dirty ? `Saved ${when(saved)}` : dirty ? "Not saved yet" : "Not graded yet"}</span>
        <button type="button" className="primary small" disabled={busy || !dirty} onClick={() => void save()}>
          {busy ? "Saving…" : "Save grade"}
        </button>
      </div>
    </section>
  );
}

function SnapshotPreview({ files }: { files: Record<string, string> }) {
  const [run, setRun] = useState(0);
  const html = useMemo(() => buildPreview(files).html, [files]);
  return (
    <section className="panel" aria-label="The submitted project">
      <div className="row between">
        <span className="eyebrow">The submitted project · try it</span>
        <button type="button" className="ghost" onClick={() => setRun((n) => n + 1)}>
          <Restart size={14} />
          Restart
        </button>
      </div>
      <div className="snapshot">
        <iframe key={run} title="Submitted project" sandbox={PREVIEW_SANDBOX} srcDoc={html} />
      </div>
      {files["PROJECT.md"] !== undefined && (
        <details className="notes-file">
          <summary>PROJECT.md (plan, rules, tests)</summary>
          <pre>{files["PROJECT.md"]}</pre>
        </details>
      )}
    </section>
  );
}

const MODE_NAMES: Record<string, string> = { build: "Build", plan: "Plan", ask: "Ask" };

function Transcript({ turns }: { turns: TranscriptTurn[] }) {
  return (
    <section className="panel" aria-label="Transcript">
      <div className="row between">
        <span className="eyebrow">Transcript · every message to the helper</span>
        <span className="muted small">{turns.length === 0 ? "none" : `${turns.length} message${turns.length === 1 ? "" : "s"}`}</span>
      </div>
      {turns.length === 0 && <p className="muted small">The student didn't ask the helper for anything before submitting.</p>}
      <ol className="transcript">
        {turns.map((t) => {
          const approval = t.mode === "build" && !t.message.trim();
          const reply = t.summary.length ? t.summary.join(" ") : t.finalText;
          return (
            <li key={t.id}>
              <div className="tmeta">
                <span className={`mode m-${t.mode}`}>{MODE_NAMES[t.mode] ?? t.mode}</span>
                <span className="muted small">{when(t.createdAt)}</span>
                {t.status !== "done" && t.status !== "answer" && t.status !== "plan" && <span className="chip todo">{t.status.replace("_", " ")}</span>}
              </div>
              <blockquote>{approval ? <i>Approved the helper's plan</i> : t.message}</blockquote>
              {t.plan?.steps.length ? (
                <ol className="tplan">
                  {t.plan.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              ) : (
                reply && <p className="treply">{reply.length > 600 ? `${reply.slice(0, 599)}…` : reply}</p>
              )}
              {t.steps.length > 0 && <p className="tsteps">{t.steps.join(" · ")}</p>}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
